import {
  AppState,
  IInstance,
  IInstanceConfig,
  IInstanceConfigData,
  IPeriod,
  ISideNavItemModel,
  IUser,
} from '@vantage-platform/store/lib/models';
import { IApp, IAppFocusBoard } from '../models/i-app';
import { Observable, of } from 'rxjs';

import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from 'apps/vantage-focus/src/environments/environment';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { user } from '../+state/app/store.selectors';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  constructor(private http: HttpClient, public store: Store<AppState>) {}

  getCategories(appId: number, params): Observable<ISideNavItemModel[]> {
    // const url = `apps/${appId}:${language}/categories`;
    const url = `me/instance-app-categories`;

    return this.http.get(`${environment.api.url}${url}`, {
      params,
    }) as Observable<ISideNavItemModel[]>;
  }

  getPeriodsData(app: IApp, instance: IInstance): Observable<IPeriod[]> {
    const defaultPeriod: IPeriod[] = [
      {
        PeriodType: '',
        PeriodName: '',
        PeriodNumber: 0,
        YearNumber: 0,
        YearName: null,
        active: true,
      },
    ];

    if (typeof instance.config === 'undefined')
      return of(defaultPeriod) as Observable<IPeriod[]>;

    const url = `me/boards/${app.defaultBoardID}/availableperiods`;

    return this.http.get(`${environment.api.url}${url}`).pipe(
      map((p: IPeriod[]) => {
        return p;
      })
    );
  }

  getUser(): Observable<IUser> {
    const url = 'me';
    return this.http.get(`${environment.config.url}${url}`).pipe(
      map((p: IUser) => {
        return p;
      })
    );
  }

  validateAccess(params) {
    const url = 'access';
    return this.http.get(`${environment.config.url}${url}`, {
      params,
      observe: 'response',
    });
  }

  getAppFeature(app: IApp) {
    const url = 'app-features';
    return this.http.get(`${environment.config.url}${url}`, {
      params: { appID: `${app.id}` },
    });
  }

  getInstances(languageId?: number): Observable<IInstance[]> {
    const fetchInstances = (resolvedLanguageId: number) => {
      const url = `me/instances?language-id=${resolvedLanguageId}`;
      return this.http.get(`${environment.config.url}${url}`).pipe(
        map((p: IInstance[]) => {
          return p;
        })
      );
    };

    if (typeof languageId === 'number') {
      return fetchInstances(languageId);
    }

    return this.store.select(user).pipe(
      take(1),
      switchMap((userData) => fetchInstances(userData?.defaultLanguageID || 1))
    );
  }

  getInstanceConfig(instance: IInstance): Observable<IInstanceConfig> {
    // 13 default instance config type
    const url = `instanceconfiguration/${instance.id}:13`;
    return this.http.get(`${environment.config.url}${url}`).pipe(
      map((r: IInstanceConfig) => {
        let newIns = { ...r };
        newIns.data = JSON.parse(r.value) as IInstanceConfigData;

        return newIns;
      })
    );
  }

  getMeApps(instanceId: number): Observable<IApp[]> {
    return this.store.select(user).pipe(
      take(1),
      switchMap((userData) => {
        const languageId = userData?.defaultLanguageID || 1;
        const url = `me/apps?language-id=${languageId}&instance-id=${instanceId}`;
        return this.http.get(`${environment.config.url}${url}`).pipe(
          map((p: IApp[]) => {
            return p;
          })
        );
      })
    );
  }

  getApps(): Observable<IApp[]> {
    return this.store.select(user).pipe(
      take(1),
      switchMap((userData) => {
        const languageId = userData?.defaultLanguageID || 1;
        const url = `apps?language-id=${languageId}`;
        return this.http.get(`${environment.config.url}${url}`).pipe(
          map((p: IApp[]) => {
            return p;
          })
        );
      })
    );
  }

  validateAccessToApp(instanceId: number, appId: number): Observable<IApp | null> {
    const url = `me/apps/id?instance-id=${instanceId}&app-id=${appId}`;
    return this.http.get<IApp>(`${environment.config.url}${url}`, { observe: 'response' }).pipe(
      map((response: HttpResponse<IApp>) => {
        // Return null for 204 No Content, otherwise return the body
        if (response.status === 204 || !response.body) {
          return null;
        }
        return response.body;
      }),
      catchError((error: HttpErrorResponse) => {
        // Rethrow other errors
        throw error;
      })
    );
  }

  getAppDefaultFocusboard(
    instanceId: number,
    appId: number
  ): Observable<IAppFocusBoard> {
    const url = `me/instance-app-default-board/${instanceId}:${appId}`;

    return this.http.get(`${environment.api.url}${url}`) as Observable<
      IAppFocusBoard
    >;
  }
}
